<script setup lang="ts">

const LazyWrContactFormPopup = defineAsyncComponent(() => import('~/components/molecules/WrContactFormPopup.vue'));
const LazyHydrationWrTestimonialCardCarousel = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrTestimonialCardCarousel.vue'));
const LazyHydrationWrTechUpgrade = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrTechUpgrade.vue'));
const LazyHydrationWrTailored = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrTailored.vue'));
const LazyHydrationWrSelectedProject = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrSelectedProject.vue'));
const LazyHydrationWrPricing = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrPricing.vue'));
const LazyHydrationWrOfficialPartners = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrOfficialPartners.vue'));
const LazyHydrationWrFromAugmentation = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrFromAugmentation.vue'));
const LazyHydrationWrDeveloper = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrDeveloper.vue'));
const LazyHydrationWrAnimatedTraining = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrAnimatedTraining.vue'));
const LazyHydrationWrAccordion = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrAccordion.vue'));
const LazyHydrationWrRelatedCapabilities = defineLazyHydrationComponent('visible', () => import('~/components/molecules/WrRelatedCapabilities.vue'));

import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

const route = useRoute();

const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services);

// Fetch content from JSON
const url = useRequestURL();
const { data: vuejsContent } = await useFetch(
  `${url.origin}/content/services/frameworks/vuejs-development-services-content.json`,
);

// Destructure content
const meta_tags = vuejsContent.value?.meta_tags ?? {};
const hero = vuejsContent.value?.hero ?? {};
const solution_cards = vuejsContent.value?.solution_cards ?? {};
const feature_cards = vuejsContent.value?.feature_cards ?? [];
const tabs_data = vuejsContent.value?.tabs ?? [];
const software_product_section = vuejsContent.value?.software_product_section ?? {};
const multitenancy_section = vuejsContent.value?.multitenancy_section ?? {};
const official_partners_heading = vuejsContent.value?.official_partners_heading ?? 'Official Partner Of';
const why_choose_us = vuejsContent.value?.why_choose_us ?? {};
const hire_section = vuejsContent.value?.hire_section ?? {};
const staff_augmentation = vuejsContent.value?.staff_augmentation ?? {};
const staff_achievements = vuejsContent.value?.staff_achievements ?? [];
const staff_specs = vuejsContent.value?.staff_specs ?? [];
const faq_section = vuejsContent.value?.faq_section ?? {};
const accordion_content = vuejsContent.value?.accordion_content ?? [];
const pricing_content = vuejsContent.value?.pricing_content ?? [];
const pricing_section = vuejsContent.value?.pricing_section ?? {};
const upgrade_section = vuejsContent.value?.upgrade_section ?? {};
const upgrade_versions = vuejsContent.value?.upgrade_versions ?? {};
const outsource_section = vuejsContent.value?.outsource_section ?? {};
const outsource_reasons = vuejsContent.value?.outsource_reasons ?? [];
const testimonial_section = vuejsContent.value?.testimonial_section ?? {};
const related_capabilities_section = vuejsContent.value?.related_capabilities_section ?? {};

// Computed items for components
const solutionItems = computed(() => solution_cards.cards ?? []);
const featureCards = computed(() => feature_cards);
const vueRelatedCapabilityItems = computed(() => vuejsContent.value?.related_capabilities ?? []);

// Meta tags setup
useHead({
  htmlAttrs: {
    lang: "en",
  },
  title: meta_tags.title ?? "Vue Web Development Services – Certified Vue Partner Agency | WebReinvent",
  meta: meta_tags.meta ?? [
    {
      name: "description",
      content: "WebReinvent is a leading Vue development company in Delhi, India, crafting high-performance, scalable, and visually elegant web applications for global brands.",
    },
    {
      name: "keywords",
      content: "Vue.JS Development Company, vue js development, vue js cdn,vue js components,laravel vue js,Vue.JS development services, Vue.js Custom Development and Maintenance",
    },
  ],
  link: vuejsContent.value?.preload_images ?? [
    {
      rel: "preload",
      as: "image",
      href: "/images/agile.webp",
      type: "image/webp",
    },
    {
      rel: "preload",
      as: "image",
      href: "/images/agile-dark.webp",
      type: "image/webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          // --- Service ---
          {
            "@type": "Service",
            name: hero.heading.replace(/<br\s*\/?>/gi, " "),
            serviceType: hero.heading.replace(/<br\s*\/?>/gi, " "),
            description: (hero.subheading ?? "").replace(/<[^>]+>/g, " ").trim(),
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
            },
            url: `https://webreinvent.com${route.path}`,
          },
          // --- FAQPage ---
          {
            "@type": "FAQPage",
            mainEntity: accordion_content.map((item: any) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description.replace(/<[^>]+>/g, " ").trim(),
              },
            })),
          },
          // --- BreadcrumbList (conditional) ---
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

// Tabs state
const tabs = ref(
  tabs_data.map((title: string, index: number) => ({
    title,
    active: index === 0,
  }))
);

const selectedTab = ref<any>(tabs.value.map((tab) => tab.active));

const handleTabClick = (index: any) => {
  tabs.value = tabs.value.map((tab, i) => ({
    ...tab,
    active: i === index,
  }));
  selectedTab.value = tabs.value.map((tab) => tab.active);
};

// Accordion filter state
const activeIndex = ref(0);

const setActive = (index: any) => {
  activeIndex.value = index;
};

const filteredAccordionContent = computed(() => {
  const selectedTag = (faq_section.filter_items ?? ["All"])[activeIndex.value];
  return accordion_content.filter((item: any) =>
    item.tags.includes(selectedTag)
  );
});

// Modal state
const visible = ref(false);
const formPrefilledData = ref(null);

const openModal = (prefilledValue = null) => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (prefilledValue.length) formPrefilledData.value = prefilledValue;
  visible.value = true;
};

const handleButtonClick = () => {
  openModal();
};

const openWithEmitVariable = (type: string) => {
  formPrefilledData.value = type;
  visible.value = true;
};

// Upgrade version data
const oldVersion = computed(() => upgrade_versions.oldVersion ?? []);
const newVersion = computed(() => upgrade_versions.newVersion ?? []);
const preVer = computed(() => upgrade_versions.preVer ?? []);
const latestVer = computed(() => upgrade_versions.latestVer ?? []);
</script>

<template>
  <div
    class="z-10 relative dark:bg-[linear-gradient(109.63deg,#0C0226_3.67%,#1C0035_83.91%)]"
  >
    <section
      class="relative z-10 py-10 lg:py-32 overflow-visible grid place-items-center"
    >
      <div class="max-w-screen-xl mx-auto px-4 w-full relative">
        <div
          class="size-[1200px] rounded-full bg-[image:var(--bg-circle-light)] absolute -top-[115%] -left-[40%] dark:shadow-[49px_11px_53.1px_15px_#130230]"
        ></div>
        <div
          class="size-[428px] opacity-60 bg-[#AE45AA80] rounded-full absolute top-[70%] -translate-y-1/2 -left-[30%] blur-[300px]"
        ></div>
        <div
          class="size-[428px] opacity-60 bg-[image:var(--bg-circle-hero-sm)] rounded-full absolute bottom-0 -right-[30%]"
        ></div>

        <div class="z-10 relative">
          <h1 class="text-center mb-8 lg:mb-16 text-3xl lg:text-[78px]">
            <WrGradientText
              :text="hero.heading"
              append-class="max-lg:py-0 lg:pb-10"
            />
            <p class="">{{ hero.subheading }}</p>
          </h1>
          <p
            class="text-base md:text-xl text-center max-w-3xl px-2 mx-auto mb-20 tracking-[0.5px]"
            v-html="hero.description"
          ></p>

          <div
            class="max-w-4xl px-2 mx-auto mb-20 tracking-[0.5px] text-center"
          >
            <p class="mb-6 text-2xl">{{ hero.free_offers_label }}</p>
            <ul
              class="flex max-sm:flex-col justify-around text-base gap-4 text-left *:max-w-max mx-auto max-w-max"
            >
              <li
                v-for="(offer, index) in hero.free_offers"
                :key="index"
                class="flex gap-2 items-center dark:border bg-white dark:bg-transparent px-4 py-0.5 max-sm:text-sm sm:py-2 rounded-full dark:border-white/40 border-black/40"
              >
                <video autoplay loop muted playsinline width="34" height="34">
                  <source src="/images/blue-tick.webm" type="video/webm" />
                </video>
                {{ offer }}
              </li>
            </ul>
          </div>

          <img
            alt="agile"
            :src="
              $colorMode.preference == 'dark'
                ? hero.image_dark
                : hero.image_light
            "
            width="777"
            height="458"
            class="z-20 relative mx-auto"
          />
        </div>
      </div>
    </section>

    <section
      class="relative z-10 pb-10 lg:pb-24 overflow-hidden grid place-items-center"
    >
      <div class="max-w-screen-xl mx-auto px-4 w-full">
        <h2 class="text-center mb-16">
          <WrGradientText
            :text="solution_cards.heading"
            append-class="pb-4"
          />
          <p class="text-lg max-w-4xl mx-auto tracking-[0.5px] lg:px-4">
            {{ solution_cards.subheading }}
          </p>
        </h2>

        <div
          class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-7 *:w-full *:h-full"
        >
          <div
            v-for="(product, index) in solutionItems"
            :key="index"
            class="gradient-border-mask relative p-px dark:before:bg-[linear-gradient(91.95deg,_rgba(61,27,96,0.5)_4.5%,_rgba(63,22,104,0.5)_96.91%)] before:bg-[linear-gradient(91.95deg,_rgba(182,164,199,0.5)_4.5%,_rgba(148,103,192,0.5)_96.91%)]"
          >
            <Card
              unstyled
              class="p-4 md:p-7 relative dark:bg-none bg-[var(--color-5)] dark:bg-transparent text-[var(--primary)] text-sm h-full rounded-xl"
            >
              <template #title>
                 <WrSVGIcon :name="product.svg"  class="mb-4 text-[var(--foreground)]" :class="product.iconAppendClass"/>
              </template>
              <template #content>
                <p class="m-0">{{ product.description }}</p>
                <NuxtLink
                  :to="product.link"
                  target="_blank"
                  class="absolute z-10 block size-full inset-0"
                />
              </template>
            </Card>
          </div>
        </div>
        <WrButton @click="handleButtonClick" container-class="mx-auto mt-6"
          >{{ solution_cards.cta_text }}</WrButton
        >
      </div>
    </section>
    <div
      class="flex gap-8 text-sm md:text-lg font-normal max-w-max mx-auto mb-14 px-4"
    >
      <p
        v-for="(tab, index) in tabs"
        :key="tab.title"
        :aria-selected="tab.active"
        :class="
          tab.active
            ? 'max-sm:text-sm text-[var(--primary)] dark:text-white font-semibold border-b-4 !pb-3 !p-0 selected_tab after:aria-selected:top-[100%]'
            : 'text-[#838385] dark:text-[#6950AA] cursor-pointer'
        "
        @click="handleTabClick(index)"
      >
        {{ tab.title }}
      </p>
    </div>
    <div class="flex flex-col">
      <!-- Section 1: Build with Vue Partner -->
      <section
        class="relative z-10 pb-10 lg:pb-24 overflow-hidden grid place-items-center"
        :class="selectedTab[0] ? 'order-1' : 'order-2'"
      >
        <div class="max-w-screen-xl mx-auto px-10 w-full">
          <h2 class="text-center mb-16">
            <WrGradientText
              :text="software_product_section.heading"
              append-class=" pb-4"
            />
            <p class="text-lg max-w-3xl mx-auto tracking-[0.5px]">
              {{ software_product_section.subheading }}
            </p>
          </h2>
          <div class="relative">
            <div
              class="grid max-sm:max-w-[350px] mx-auto max-sm:place-content-left sm:grid-cols-2 lg:grid-cols-4 relative"
            >
              <LazyHydrationWrTailored :content="featureCards" variant="secondary" :hydrate-on-visible="{ rootMargin: '100px' }"/>
            </div>

            <WrButton
              @click="handleButtonClick"
              container-class="mx-auto mt-6 z-10 relative"
              >{{ software_product_section.cta_text }}</WrButton
            >
          </div>
        </div>
      </section>

      <section
        class="relative z-10 pb-20 lg:pb-32 grid place-items-center order-3"
      >
        <div class="max-w-screen-xl mx-auto px-4 w-full">
          <div
            class="size-[428px] opacity-60 bg-[#AE45AA80] rounded-full absolute top-[50%] left-1/2 -translate-x-1/2 blur-[300px]"
          ></div>
          <h2 class="text-center">
            <WrGradientText
              :text="multitenancy_section.heading"
              append-class="text-3xl lg:text-[48px] py-0 lg:pb-4"
            />
            <p class="text-3xl lg:text-[48px]">
              {{ multitenancy_section.subheading }}
            </p>
          </h2>
          <p
            class="mt-6 text-lg max-w-4xl mx-auto text-center tracking-[0.5px]"
          >
            {{ multitenancy_section.description }}
          </p>
          <NuxtImg
            :src="multitenancy_section.image"
            alt="agile"
            width="777"
            height="458"
            class="mx-auto mt-12"
            loading="lazy"
            format="webp"
          />

          <LazyHydrationWrOfficialPartners
            append-class="mt-8 md:mt-16"
            :title="official_partners_heading"
            :partnersAppendClass="'justify-center items-center'"
            :hydrate-on-visible="{ rootMargin: '100px' }"
          />
        </div>
        <div class="max-w-screen-xl mx-auto px-4 pt-14">
          <h2 class="text-center">
            <WrGradientText :text="why_choose_us.heading" append-class=" py-0" />
          </h2>
          <p
            class="mt-4 text-lg max-w-4xl mx-auto text-center tracking-[0.5px]"
            v-html="why_choose_us.subheading"
          ></p>
          <div class="py-8">
            <div class="max-w-7xl mx-auto px-6">
              <!-- Top Stats -->
              <div
                class="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center mb-12"
              >
                <div
                  v-for="(stat, index) in why_choose_us.stats"
                  :key="index"
                >
                  <h3 class="text-3xl font-bold text-[var(--primary)]">{{ stat.figure }}</h3>
                  <p class="text-gray-700 dark:text-white/50">{{ stat.text }}</p>
                </div>
              </div>

              <!-- Bottom Feature Boxes -->
              <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                  v-for="(box, index) in why_choose_us.feature_boxes"
                  :key="index"
                  class="bg-[image:var(--border-staff-card)] rounded-xl h-full text-white shadow-md relative p-px"
                >
                  <div
                    class="py-6 px-4 rounded-xl text-center bg-[image:var(--bg-staff-card)] h-full"
                  >
                    <p class="text" v-html="box"></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 2: Hire Certified Vue Professionals -->
      <section
        class="relative z-10 pb-10 lg:pb-24 grid place-items-center"
        :class="selectedTab[1] ? 'order-1' : 'order-4'"
      >
        <div class="max-w-screen-xl mx-auto px-4 w-full">
          <div
            class="max-sm:hidden size-[598px] bg-[#AE45AA]/10 rounded-full absolute top-[20%] left-0 -translate-x-1/2 blur-[80px]"
          ></div>
          <h3 class="text-center">
            <WrGradientText
              :text="hire_section.heading"
              append-class=" py-0 lg:py-4"
            />
            <p class="text-3xl lg:text-[48px]">{{ hire_section.subheading }}</p>
          </h3>
          <p
            class="mt-6 text-lg max-w-3xl mx-auto text-center tracking-[0.5px]"
          >
            {{ hire_section.description }}
          </p>
            <LazyHydrationWrAnimatedTraining :content="'vue'" :hydrate-on-visible="{ rootMargin: '100px' }"/>
        </div>
        <div class="overflow-x-auto w-full px-4 no-scrollbar">
          <div class="flex gap-6 mt-16 mx-auto max-w-max">
            <LazyHydrationWrDeveloper :dev-info="vuejsContent.value?.devInfo" :hydrate-on-visible="{ rootMargin: '100px' }"/>
          </div>
        </div>
        <WrButton container-class="mx-auto mt-12" @click="handleButtonClick"
          >{{ hire_section.cta_text }}</WrButton
        >
      </section>

      <!-- Section 3: IT Staff Augmentation -->
      <section
        class="relative z-10 pb-20 lg:pb-36 overflow-hidden grid place-items-center"
        :class="selectedTab[2] ? 'order-1' : 'order-5'"
      >
        <div class="max-w-screen-xl mx-auto px-4 w-full">
          <h3 class="text-center mb-0 lg:mb-11">
            <WrGradientText
              :text="staff_augmentation.heading"
              append-class="text-5xl lg:text-[120px] bg-[image:var(--text-secondary-gradient)]"
            />
          </h3>
          <div class="flex flex-col lg:flex-row gap-10 justify-between">
            <div class="mt-4 max-lg:text-center">
              <p class="text-3xl md:text-[3rem] md:min-w-max leading-tight" v-html="staff_augmentation.subheading"></p>
              <p class="text-medium md:mt-3 tracking-[0.5px]">
                {{ staff_augmentation.description }}
              </p>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-white">
              <div
                v-for="item in staff_achievements"
                :key="item.title"
                class="bg-[image:var(--border-staff-card)] rounded-xl p-px h-full text-white"
              >
                <div
                  class="py-6 px-4 rounded-xl text-center bg-[image:var(--bg-staff-card)] h-full"
                >
                  <p class="font-semibold text-5xl mdtext-6xl mb-2">
                    {{ item.count }}
                  </p>
                  <p class="text-medium md:text-lg line-clamp-3">
                    {{ item.title }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="grid sm:grid-cols-2 lg:grid-cols-4 my-12 gap-y-4">
            <div
              v-for="item in staff_specs"
              :key="item.title"
              class="flex gap-7 px-7 py-5 border-l border-[var(--color-10)] relative after:content-[''] after:absolute after:top-6 after:-left-px after:w-px after:h-4 after:bg-[var(--color-11)]"
            >
              <WrSVGIcon :class="item.appendClass" :name="item.svg" class="shrink-0 text-[#7758AE] dark:text-[var(--foreground)]"/>
              <p
                class="text-sm text-[var(--foreground)] dark:text-[var(--primary)] tracking-[0.5px]"
              >
                {{ item.title }}
              </p>
            </div>
          </div>

          <LazyHydrationWrFromAugmentation @formSubmitted="openModal" :hydrate-on-visible="{ rootMargin: '100px' }"/>
        </div>
      </section>

      <section
        class="relative pb-10 lg:pb-24 overflow-visible grid place-items-center order-6"
      >
        <div
          v-if="$colorMode.preference == 'dark'"
          class="absolute w-[2266px] h-[1241px] top-20 rounded-[100%] bg-[linear-gradient(0deg,_#1F013405_31.11%,_#4D27B600_51.33%,_#855BF200_57.98%,_#22206D20_62.05%)] dark:bg-[linear-gradient(0deg,_#1F0134_31.11%,_#4D27B600_51.33%,_#855BF200_57.98%,_#22206D_62.05%)] blur-[100px] dark:blur-[100px]"
        >
          <div
            class="w-[2260px] h-[1440px] bg-white dark:bg-[var(--color-5)] rounded-[100%] absolute top-[200px] left-0"
          ></div>
        </div>
        <NuxtImg
          v-if="$colorMode.preference != 'dark'"
          src="/images/cms-back-laravel.webp"
          format="webp"
          class="absolute -top-96 left-1/2 -translate-x-1/2 min-w-[3745px] min-h-[2349]"
          width="3745"
          height="2349"
          alt="cms-back"
          loading="lazy"
        />

        <div class="rising-stars top-0">
          <div style="height: 4px; width: 4px"></div>
          <div style="height: 2px; width: 2px"></div>
          <div style="height: 1px; width: 1px"></div>
        </div>

        <div
          class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10"
        >
          <div class="max-md:text-center mx-auto max-w-[504px]">
            <h3>
              <WrGradientText
                :text="faq_section.heading"
                append-class="py-0 md:py-4"
              />
              <span>{{ faq_section.heading_suffix }}</span>
            </h3>
            <p class="mt-5 pr-4 text-lg tracking-[0.5px]" v-html="faq_section.subheading"></p>

            <ul
              class="mt-8 list-disc max-md:gap-y-4 max-md:max-w-max max-md:mx-auto max-md:grid max-md:grid-cols-2 text-[var(--secondary-foreground)] md:space-y-4 list-inside text-left"
            >
              <li
                v-for="(item, index) in faq_section.filter_items"
                :key="index"
                @click="setActive(index)"
                :class="{
                  'cursor-pointer': true,
                  active: activeIndex === index,
                }"
              >
                {{ item }}
              </li>
            </ul>
            <WrButton
              container-class="mt-8 max-md:mx-auto"
              @click="handleButtonClick"
              >{{ faq_section.cta_text }}</WrButton
            >
          </div>
          <div class="grow">
            <LazyHydrationWrAccordion :Content="filteredAccordionContent" :hydrate-on-visible="{ rootMargin: '100px' }"/>
          </div>
        </div>
      </section>

      <section
        class="relative z-10 py-10 lg:pb-20 grid place-items-center order-7"
      >
        <div class="max-w-screen-lg mx-auto overflow-hidden px-4">
          <h3 class="text-center">
            <WrGradientText
              :text="pricing_section.heading"
              append-class="py-0 md:py-4"
            />
            <p class="">{{ pricing_section.heading_suffix }}</p>
          </h3>
          <p class="text-lg text-center mb-10 mt-4 tracking-[0.5px]" v-html="pricing_section.subheading"></p>
        </div>
        <div class="overflow-x-auto w-full px-4 pt-12 no-scrollbar">
          <div class="flex gap-6 mt-8 mx-auto max-w-max py-4">
            <LazyHydrationWrPricing
              :pricing-content="pricing_content"
              class="w-full"
              @openPopup="openWithEmitVariable"
              :hydrate-on-visible="{ rootMargin: '100px' }"
            />
          </div>
        </div>
      </section>

      <section
        class="relative z-20 py-10 lg:pb-40 overflow-hidden grid place-items-center order-8"
      >
        <div class="max-w-screen-xl mx-auto px-4 w-full">
          <h4 class="text-center">
            <WrGradientText
              :text="upgrade_section.heading"
              append-class="py-0 md:py-4"
            />
            <p class="">{{ upgrade_section.heading_suffix }}</p>
          </h4>
          <p class="text-lg text-center mb-8 md:mb-20 mt-7 tracking-[0.5px]">
            {{ upgrade_section.subheading }}
          </p>
          <LazyHydrationWrTechUpgrade
            :pre-ver="preVer"
            :latest-ver="latestVer"
            :old-version="oldVersion"
            :new-version="newVersion"
            :hydrate-on-visible="{ rootMargin: '100px' }"
          />
        </div>
      </section>

      <section class="relative z-10 py-10 lg:pb-28 overflow-visible order-9">
        <div
          class="max-w-screen-lg mx-auto px-4 w-full relative max-md:max-w-full overflow-auto"
        >
          <div
            class="hidden dark:block opacity-30 md:size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
          ></div>

          <div
            class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] right-0 top-0"
          ></div>
          <LazyHydrationWrSelectedProject :hydrate-on-visible="{ rootMargin: '100px' }"/>
        </div>
      </section>

      <section
        class="relative z-10 py-10 lg:pb-32 overflow-hidden grid place-items-center order-10"
      >
        <div class="max-w-screen-xl mx-auto px-4 w-full">
          <h5 class="text-center">
            <WrGradientText :text="outsource_section.heading" />
          </h5>
          <div class="grid md:grid-cols-2 gap-8 max-w-max mx-auto mt-10">
            <div v-for="reason in outsource_reasons" :key="reason.id" class="flex items-center gap-5">
              <div
                class="p-px bg-[#957EFF] dark:bg-transparent dark:bg-[image:var(--border-form-card)] rounded-full"
              >
                <div
                  class="size-16 md:size-20 rounded-full bg-white dark:bg-[#200945] grid place-items-center text-2xl"
                >
                  {{ reason.id }}.
                </div>
              </div>
              <p
                v-html="reason.text"
                class="md:text-base tracking-[0.5px] text-[var(--secondary-foreground)]"
              ></p>
            </div>
          </div>
        </div>
      </section>
    </div>

    <section
      class="relative py-10 lg:py-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
    >
      <h5 class="text-center">
        <WrGradientText :text="testimonial_section.heading" />
      </h5>
      <p
        class="text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
      >
        {{ testimonial_section.subheading }}
      </p>
      <!-- Carousel Section -->

      <LazyHydrationWrTestimonialCardCarousel />

      <WrButton :link="testimonial_section.cta_link" container-class="max-w-max mx-auto mt-2"
        >{{ testimonial_section.cta_text }}</WrButton
      >
    </section>

    <section
      class="relative z-10 py-10 lg:py-20 overflow-hidden grid place-items-center"
    >
      <div class="max-w-screen-xl mx-auto px-4 w-full">
        <LazyHydrationWrRelatedCapabilities
          :items="vueRelatedCapabilityItems"
          :exclude="related_capabilities_section.exclude"
          :heading="related_capabilities_section.heading"
          :subtitle="related_capabilities_section.subtitle"
          :hydrate-on-visible="{ rootMargin: '100px' }"
        />
      </div>
    </section>
  </div>
  <LazyWrContactFormPopup
  v-if="visible"
    :isOpen="visible"
    @close="visible = false"
    class="z-30"
    :prefilledContent="formPrefilledData"
  />
</template>
